import React, {useState, useCallback, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useLazyQuery  } from '@apollo/client';
import { View, Text, Pressable } from 'react-native';
import { useParams } from 'react-router-dom';

import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { useNavigate } from 'app/src/utils/routing';

import AssignToUsers from './AssignToUsers';
import { UPDATE_ASSET, SELECTS, LOCATION_SELECTS } from './queries';
import SubNav from './SubNav';
import { AssetLeftContainer, AssetRightContainer, ButtonContainer } from './elements';
import {NewAssetDocuments} from '../Attachments/new';
import CustomLocation from '../Locations/CustomLocation';
import { getLocation } from 'graphql';
import formattedLocation from './formattedLocation';
import moment, { isDate } from 'moment';

const EditAsset = ({ asset, refetch, setShowModal }) => {
  const [submitting, setSubmitting] = useState();
  const [selectedSellerUsers, setSelectedSellerUsers] = useState([]);
  const [locations, setLocations] = useState();
  const [showCustomLocationField, setShowCustomLocationField] = useState(
    !asset.location
  );
  const [showCustomConsignmentLocation, setShowCustomConsignmentLocation] = useState(
    asset.customConsignmentAddress
  );
  const [sellerDocuments, setSellerDocuments] = useState(
    asset.sellerDocuments?.map(file => {
      let notes = asset.sellerDocumentNotes.filter(note => {
        if(note.attachmentId === file.id) {
          return note.body
        }
      })
      return {
        id: file.id,
        filename: file.filename,
        url: file.url,
        notes: notes.length ? notes[0].body : null
      }
    }) || []
  );

  const [updateAsset] = useMutation(UPDATE_ASSET, {
    onCompleted: () => {
      setShowModal(false);
      refetch();
    }
  });

  const [ loadSelectOptions, { loading: loading, data: nearbyData } ] =
    useLazyQuery(SELECTS, {
      onCompleted: (response) => {
      }
    }
  );

  const [ loadLocationOptions, { loading: locationsLoading, data: locationsData } ] =
    useLazyQuery(LOCATION_SELECTS, {
      onCompleted: (response) => {
      }
    }
  );

  const onSubmit = useCallback(input => {
    input['sellerUserIds'] = selectedSellerUsers;
    if(input['sellerDocuments']) {
      input['sellerDocumentNotes'] = input['sellerDocuments'].map(document => {
        return document.notes || ''
      });
      if(!input['sellerDocumentNotes']) {
        input['sellerDocumentNotes'] = [];
      }
      input['sellerDocuments'] = input['sellerDocuments'].map(document => {
        return document
      })
    }
    input['customLocation'] = showCustomLocationField;
    if(input['uccExpiresOn'] === "") {
      input['uccExpiresOn'] = null;
    }
    updateAsset({ variables: input });
  });

  const { control, handleSubmit, setValue, watch, register,
      formState: { errors }
  } = useForm({
    defaultValues: {
      id: asset.id,
      clientNumber: asset.clientNumber,
      sellerNotes: asset.sellerNotes || '',
      sellerDocuments: sellerDocuments,
      sellerDocumentNotes: asset.sellerDocumentNotes || [],
      consignmentMethod: asset.consignmentMethod || "Dropoff",
      drivable: asset.drivable || false,
      locationId: asset.location?.id || '',
      consignmentLocationId: asset.consignmentLocation?.id || '',
      customConsignmentAddress: asset.customConsignmentAddress || '',
      street: asset.street || '',
      city: asset.city || '',
      state: asset.state || '',
      postalCode: asset.postalCode.formatted || '',
      uccExpiresOn: asset.uccExpiresOn || undefined
    }
  });

  useEffect(() => {
    if (asset) {
      setSelectedSellerUsers(asset.assetSellerUsers.map(
        assetUser => assetUser.sellerUser.id
      ));
    }
  }, [asset]);

  useEffect(() => {
    if(!locationsData) {
      loadLocationOptions();
    }
    if(locationsData) {
      setLocations(locationsData?.locations)
    }
  }, [locationsData])

  const watchConsignmentMethod = watch('consignmentMethod');
  const watchDrivable = watch('drivable');
  const watchStreet = watch('street');
  const watchCity = watch('city');
  const watchState = watch('state');
  const watchZip = watch('postalCode');

  useEffect(() => {
    if(watchCity || watchZip) {
      loadSelectOptions({
        variables: {
          address: [watchStreet, watchCity, watchState, watchZip].filter(Boolean).join(", ") ||
          null
        }
      });
    }
  }, [watchCity, watchZip])

  if (submitting) { return <DataState.Loading />; }

  return (
    <View>
      <Input.Text
        label={`${asset.seller.clientNumberLabelFull}:`}
        name="clientNumber"
        control={control}
      />
      <Input.Text
        label="Seller Notes:"
        name="sellerNotes"
        control={control}
      />
      {getLocation()}
      <br />
      <Input.Picker
        label="Is the Asset to be dropped off or does EquipLinc need to pick it up?"
        name="consignmentMethod"
        control={control}
        errors={errors}
        options={
          ["Dropoff", "Pickup"].map(option => {
            return {value: option, label: option}
          })
        }
      />
      <Input.Checkbox
        label="Is Asset Drivable?"
        name="drivable"
        checked={watchDrivable}
        control={control}
        errors={errors}
      />
      {!showCustomConsignmentLocation && (nearbyData || locations) &&
        <Input.Picker
          label={
            watchConsignmentMethod === 'Dropoff' ?
              'Dropoff Location' :
              'Pickup Location'
          }
          name="consignmentLocationId"
          control={control}
          errors={errors}
          options={getLocationOptions(true)}
        />
      }
      {showCustomConsignmentLocation &&
        <Input.Text
          label='Custom Pickup Location'
          name="customConsignmentAddress"
          control={control}
        />
      }
      {watchConsignmentMethod === 'Pickup' &&
        <View>
          {!showCustomConsignmentLocation ?
            <Pressable
              onPress={() => {
                setValue('consignmentLocationId', null);
                setShowCustomConsignmentLocation(true)
              }}
            >
              <Text
                style={{
                  fontSize: '14px',
                  marginTop: '-5px',
                  marginLeft: '10px'
                }}
              >
                Custom Pickup Location
              </Text>
            </Pressable> :
            <Pressable
            onPress={() => {
              setValue('customConsignmentAddress', null);
              setShowCustomConsignmentLocation(false)
            }}>
              <Text
                style={{
                  fontSize: '14px',
                  marginTop: '-5px',
                  marginLeft: '10px'
                }}
              >
                Select Existing Location
              </Text>
            </Pressable>
          }
        </View>
      }
      <Input.Date
        label="UCC/Title Expiration Date"
        name="uccExpiresOn"
        control={control}
        errors={errors}
        {...register("uccExpiresOn", {
          valueAsDate: true,
        })}
      />
      <br />
      <Text
        style={{marginBottom: '-15px', fontSize: '16px'}}>
        Files:
      </Text>
      {/* <br />
        {expense.expenseFiles && expense.expenseFiles.map(file =>
          <View style={{width: 'fit-content'}}>
            <Link to={file.url} target='_blank'>
              <Text>
                {file.filename}
              </Text>
            </Link>
          </View>
        )} */}
      <View>
        <NewAssetDocuments
          recordId={null}
          recordType='Asset'
          attachmentType='Seller Document'
          refetch={refetch}
          name='sellerDocuments'
          setValue={setValue}
          existingFiles={sellerDocuments}
          existingFileNotes={asset?.sellerDocumentNotes}
        />
      </View>
      <AssignToUsers
        selectedSellerUsers={selectedSellerUsers}
        setSelectedSellerUsers={setSelectedSellerUsers}
      />
      <ButtonContainer>
        <ButtonGroup>
          <Button label="Update Asset" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </ButtonContainer>
    </View>
  );

  function getLocation() {
    if(asset.location || (asset.city && asset.state)) {
      return (
        <div style={{marginTop: '10px', marginBottom: '-5px'}}>
          {`Location: ${formattedLocation(asset)}`}
        </div>
      )
    }else {
      return (
        <CustomLocation
          asset={asset}
          control={control}
          errors={errors}
        />
      )
    }
  }

  function getLocationOptions(forNearbys) {
    let locs = forNearbys && nearbyData ?
      nearbyData?.nearbyLocations[0].locations :
      locations

    locs = locs?.map(location => {
      return {
        value: location.id,
        label: [
          location.name,
          location.address,
          getLocationDistance(
            location.id,
            nearbyData ? nearbyData.nearbyLocations[0].distances : []
          )
        ].filter(Boolean).join(' - ')
      }
    })
    locs?.unshift({value: null, label: 'Select Location'});

    return locs;
  }

  function getLocationDistance(locationId, distances) {
    let distance = null;
    distances?.map(d => {
      if(parseInt(d.id) === parseInt(locationId)) {
        distance = d.distance;
      }
    })
    return distance ? `${parseFloat(distance).toFixed(0)} miles` : null;
  }
};

export default EditAsset;
